<div class="planning-overview-container">
  <header class="header">
    <div class="header-content flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
      <div>
        <h1>
          <span class="material-symbols-outlined">assignment</span>
          {{ pageTitleText() }}
        </h1>
        <p class="text-slate-500 mt-1 text-sm">{{ pageSubtitleText() }}</p>
      </div>

      <!-- Action Controls -->
      <div class="flex items-center gap-3 self-start md:self-center">
        <!-- View Toggle Switch -->
        <div class="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200">
          <button (click)="viewMode.set('table')"
                  [class.bg-white]="viewMode() === 'table'"
                  [class.shadow-sm]="viewMode() === 'table'"
                  [class.text-blue-600]="viewMode() === 'table'"
                  class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-xs font-semibold text-slate-600 hover:text-blue-600">
            <span class="material-symbols-outlined text-base">table_view</span>
            <span>Tabelle</span>
          </button>
          <button (click)="viewMode.set('calendar')"
                  [class.bg-white]="viewMode() === 'calendar'"
                  [class.shadow-sm]="viewMode() === 'calendar'"
                  [class.text-blue-600]="viewMode() === 'calendar'"
                  class="flex items-center gap-2 px-4 py-1.5 rounded-lg transition-all text-xs font-semibold text-slate-600 hover:text-blue-600">
            <span class="material-symbols-outlined text-base">calendar_month</span>
            <span>Kalender</span>
          </button>
        </div>

        <!-- Export Button -->
        @if (plannedBlocks().length > 0) {
          <button (click)="exportToICS()" 
                  class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95">
            <span class="material-symbols-outlined text-base">download</span>
            <span>Kalender exportieren (.ics)</span>
          </button>
        }
      </div>
    </div>
  </header>

  <main class="content-card">
    @if (loading()) {
      <div class="flex flex-col items-center justify-center p-12 gap-4">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <span class="text-slate-600 text-sm">Daten werden geladen...</span>
      </div>
    } @else if (error()) {
      <div class="flex flex-col items-center justify-center p-12 gap-3 text-red-600 bg-red-50 rounded-xl">
        <span class="material-symbols-outlined text-4xl">error</span>
        <span class="font-medium text-sm">{{ error() }}</span>
      </div>
    } @else if (plannedBlocks().length === 0) {
      <div class="flex flex-col items-center justify-center p-12 gap-4 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
        <span class="material-symbols-outlined text-6xl">event_busy</span>
        <div class="text-center">
          <h3 class="text-lg font-semibold text-slate-900">Keine geplanten Termine</h3>
          <p class="text-sm">Für den aktuellen Zeitraum liegen keine Planungsvorschläge vor.</p>
        </div>
      </div>
    } @else {
      <!-- Legend Bar -->
      <div class="calendar-legend-bar flex flex-wrap items-center gap-x-5 gap-y-2.5 px-6 py-4 bg-slate-50 border-b border-slate-200">
        <div class="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Legende:</div>
        
        <div class="flex items-center gap-1.5">
          <span class="material-symbols-outlined text-green-600 text-lg font-bold">verified</span>
          <span class="text-xs text-slate-600 font-semibold">Garantiert</span>
        </div>
        
        <div class="flex items-center gap-1.5">
          <span class="material-symbols-outlined text-amber-500 text-lg font-medium">pending</span>
          <span class="text-xs text-slate-600 font-semibold">Provisorisch</span>
        </div>

        <div class="hidden sm:block w-px h-4 bg-slate-200 mx-1"></div>

        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded bg-orange-100 border border-orange-300"></span>
          <span class="text-xs text-slate-600 font-medium">Entwurf (DRAFT)</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span>
          <span class="text-xs text-slate-600 font-medium">Freigegeben (APPROVED)</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded bg-sky-100 border border-sky-300"></span>
          <span class="text-xs text-slate-600 font-medium">Bestätigt (Provisorisch)</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded bg-indigo-700 border border-indigo-800"></span>
          <span class="text-xs text-slate-600 font-medium">Bestätigt (Garantiert)</span>
        </div>
      </div>

      <!-- Conditional Rendering: Table vs Calendar -->
      @if (viewMode() === 'table') {
        <!-- Table View -->
        <div class="overflow-x-auto rounded-b-xl border-t-0 border border-slate-200 bg-white">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200">
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Garantiert</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Wochentag</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Datum</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Zeit</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Modul</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Standort</th>
                <th class="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200">
              @for (block of plannedBlocks(); track block.lecture_block_id) {
                <tr class="hover:bg-slate-50/50 transition-colors">
                  <td class="px-6 py-4">
                    <div class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold"
                         [ngClass]="{
                           'bg-orange-100 text-orange-700': block.planning_status === 'DRAFT',
                           'bg-purple-100 text-purple-700': block.planning_status === 'APPROVED',
                           'bg-indigo-100 text-indigo-700': block.planning_status === 'CONFIRMED',
                           'bg-red-100 text-red-700': block.planning_status === 'DECLINED'
                         }">
                      {{ 'planning.status.' + block.planning_status.toLowerCase() | translate }}
                    </div>
                  </td>
                  <td class="px-6 py-4 text-center">
                    @if (block.is_guaranteed) {
                      <span class="material-symbols-outlined text-green-600 font-bold animate-pulse" title="Garantiert">verified</span>
                    } @else {
                      <span class="material-symbols-outlined text-amber-500 font-medium" title="Provisorisch">pending</span>
                    }
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-600 font-bold uppercase">
                    {{ (block.date | date:'EE') | uppercase }}
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-600 font-medium">
                    {{ block.date | date:'dd.MM.yyyy' }}
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-600">
                    {{ block.begin_time.substring(0,5) }} - {{ block.end_time.substring(0,5) }}
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-900 font-semibold">
                    {{ block.module }}
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                    <span [class.bg-green-50]="block.location === 'ONLINE'"
                          [class.text-green-700]="block.location === 'ONLINE'"
                          [class.border-green-200]="block.location === 'ONLINE'"
                          [class.border]="block.location === 'ONLINE'"
                          [class.px-2.5]="block.location === 'ONLINE'"
                          [class.py-0.5]="block.location === 'ONLINE'"
                          [class.rounded-full]="block.location === 'ONLINE'"
                          class="inline-flex items-center gap-1 font-semibold">
                      @if (block.location === 'ONLINE') {
                        <span class="material-symbols-outlined text-[14px]">devices</span>
                      }
                      {{ block.location }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    @if (block.planning_status === 'APPROVED') {
                      <div class="flex items-center gap-2">
                        <button (click)="onAction(block, 'APPROVE')" 
                                class="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Bestätigen">
                          <span class="material-symbols-outlined">check_circle</span>
                        </button>
                        <button (click)="onAction(block, 'DECLINE')" 
                                class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Ablehnen">
                          <span class="material-symbols-outlined">cancel</span>
                        </button>
                      </div>
                    } @else if (block.planning_status === 'DRAFT') {
                      <span class="text-slate-400 text-xs italic font-medium">Warten auf Freigabe</span>
                    } @else {
                      <div class="flex items-center gap-1 text-slate-400">
                        <span class="material-symbols-outlined text-sm">lock</span>
                        <span class="text-xs">Erledigt</span>
                      </div>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else if (viewMode() === 'calendar') {
        <!-- Quarterly Calendar View -->
        <div class="calendar-grid p-6 bg-slate-50/30 rounded-b-xl border border-t-0 border-slate-200 flex flex-col gap-6">
          @for (month of calendarMonths(); track month.name) {
            <div class="shared-calendar-month-card">
              <!-- Month Header -->
              <div class="shared-calendar-month-header">
                {{ month.name }} {{ month.year }}
              </div>
              
              <!-- Weekday Header -->
              <div class="shared-calendar-weekdays">
                <div>MO</div>
                <div>DI</div>
                <div>MI</div>
                <div>DO</div>
                <div>FR</div>
                <div>SA</div>
                <div>SO</div>
              </div>
              
              <!-- Days Grid -->
              <div class="shared-calendar-grid">
                @for (week of month.weeks; track $index) {
                  @for (day of week; track day.date.getTime()) {
                    <div class="shared-calendar-day min-h-[100px]"
                         [class.out-of-month]="!day.isCurrentMonth"
                         [class.is-weekend]="day.date.getDay() === 0 || day.date.getDay() === 6"
                         [class.is-today]="day.isToday">
                      
                      <!-- Day Header (Number & Mini Indicators) -->
                      <div class="flex items-center justify-between mb-1.5 w-full">
                        <span class="shared-calendar-day-number">{{ day.date | date:'d' }}</span>
                        
                        @if (day.blocks.length > 0) {
                          <div class="flex gap-0.5">
                            @for (b of day.blocks; track b.lecture_block_id) {
                              <span class="material-symbols-outlined text-[12px] font-bold"
                                    [ngClass]="b.is_guaranteed ? 'text-green-600 animate-pulse' : 'text-amber-500'"
                                    [title]="b.is_guaranteed ? 'Garantiert' : 'Provisorisch'">
                                    {{ b.is_guaranteed ? 'verified' : 'pending' }}
                              </span>
                            }
                          </div>
                        }
                      </div>
                      
                      <!-- Planned Blocks List -->
                      <div class="flex flex-col gap-1.5 flex-1 justify-end">
                        @for (b of day.blocks; track b.lecture_block_id) {
                          <div class="cal-block-badge p-1.5 rounded-lg border text-[10px] font-bold leading-tight cursor-default transition-all shadow-sm flex flex-col gap-0.5"
                               [ngClass]="{
                                 'bg-orange-50 border-orange-200 text-orange-800': b.planning_status === 'DRAFT',
                                 'bg-purple-50 border-purple-200 text-purple-800': b.planning_status === 'APPROVED',
                                 'bg-sky-50 border-sky-200 text-sky-800 border-dashed': b.planning_status === 'CONFIRMED' && !b.is_guaranteed,
                                 'bg-indigo-700 border-indigo-800 text-white': b.planning_status === 'CONFIRMED' && b.is_guaranteed,
                                 'bg-red-100 border-red-300 text-red-800 font-bold': b.planning_status === 'DECLINED'
                               }">
                            
                            <!-- Time & Location -->
                            <div class="flex items-center justify-between font-mono font-medium text-[8px] opacity-85">
                              <span>{{ b.begin_time.substring(0,5) }} - {{ b.end_time.substring(0,5) }}</span>
                            </div>

                            <div class="flex items-center gap-1 font-mono font-semibold text-[8px] opacity-90"
                                 [class.text-green-700]="b.location === 'ONLINE' && b.planning_status !== 'CONFIRMED'">
                              <span class="material-symbols-outlined text-[10px]">
                                {{ b.location === 'ONLINE' ? 'devices' : 'location_on' }}
                              </span>
                              <span class="truncate">{{ b.location }}</span>
                            </div>

                            <!-- Module -->
                            <div class="truncate text-[9px] font-bold mt-0.5" [title]="b.module">
                              {{ b.module }}
                            </div>

                            <!-- Actions inside Badge -->
                            @if (b.planning_status === 'APPROVED') {
                              <div class="flex items-center justify-end gap-2 mt-1.5 border-t border-blue-200/50 pt-1">
                                <button (click)="onAction(b, 'APPROVE'); $event.stopPropagation()" 
                                        class="text-green-600 hover:text-green-800 hover:bg-green-100/60 p-0.5 rounded transition-all active:scale-90"
                                        title="Bestätigen">
                                  <span class="material-symbols-outlined text-[14px] font-bold">check_circle</span>
                                </button>
                                <button (click)="onAction(b, 'DECLINE'); $event.stopPropagation()" 
                                        class="text-red-600 hover:text-red-800 hover:bg-red-100/60 p-0.5 rounded transition-all active:scale-90"
                                        title="Ablehnen">
                                  <span class="material-symbols-outlined text-[14px] font-bold">cancel</span>
                                </button>
                              </div>
                            } @else if (b.planning_status === 'DRAFT') {
                              <span class="text-[8px] font-normal italic opacity-80 mt-0.5 text-orange-700">Warten auf Freigabe</span>
                            } @else {
                              <div class="flex items-center justify-end gap-0.5 text-[8px] opacity-75 mt-0.5">
                                <span class="material-symbols-outlined text-[9px]">lock</span>
                                <span>Erledigt</span>
                              </div>
                            }
                          </div>
                        }
                      </div>
                      
                    </div>
                  }
                }
              </div>
            </div>
          }
        </div>
      }
    }
  </main>
</div>
